import { Injectable, inject } from '@angular/core';
import { AuthenticationFacade } from '@navfleet/client/authentication/domain/data-access';
import { shareLast } from '@navfleet/client/shared/util/functions';
import { Subject, distinctUntilChanged, filter, map, startWith, switchMap, take } from 'rxjs';

interface StorageMap<T> {
    [guid: string]: T | undefined;
}

@Injectable({ providedIn: 'root' })
export class LocalStorageService {
    private readonly authenticationFacade = inject(AuthenticationFacade);

    private readonly guid$ = this.authenticationFacade.getUserGuid(false).pipe(shareLast());
    private readonly updateSubject$ = new Subject<string>();

    public get<T>(key: string) {
        return this.updateSubject$.pipe(
            filter((updateKey) => updateKey === key),
            startWith(null),
            switchMap(() => this.guid$),
            map((guid) => this.getStorageMap<T>(key)[guid]),
            distinctUntilChanged(),
            shareLast()
        );
    }

    public getWithDefault<T>(key: string, defaultValue: T) {
        return this.get<T>(key).pipe(map((value) => value ?? defaultValue));
    }

    public set<T>(key: string, value: T) {
        this.guid$.pipe(take(1)).subscribe((guid) => {
            localStorage.setItem(key, JSON.stringify({ ...this.getStorageMap(key), [guid]: value }));
            this.updateSubject$.next(key);
        });
    }

    private getStorageMap<T>(key: string): StorageMap<T> {
        return JSON.parse(localStorage.getItem(key) as string) ?? {};
    }
}
